import { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import Pagination from '@/components/Pagination/index';
import Toast from '@/utils/toastManager';
import { Select, MenuItem, FormControl } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { PORTAL_STATUS } from '@/Pages/admin/ChargebeePortal/constants/portalUiConstants';

const ReconciliationDashboard = () => {
  const [stats, setStats] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ issueType: '', severity: '' });
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const userData = useAtomValue(userInfoAtom);

  useEffect(() => {
    fetchStats();
    fetchIssues();
  }, [filters, page, itemsPerPage]);

  const fetchStats = async () => {
    const response = await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/reconciliation/dashboard`,
      {
        headers: { Authorization: `Bearer ${userData.token}` }
      }
    );
    const data = await response.json();
    setStats(data.data);
  };

  const fetchIssues = async () => {
    setLoading(true);
    const params = new URLSearchParams({ ...filters, page, limit: itemsPerPage });
    const response = await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/reconciliation/issues?${params}`,
      {
        headers: { Authorization: `Bearer ${userData.token}` }
      }
    );
    const data = await response.json();
    setIssues(data.data.issues);
    setTotal(data.data.total);
    setLoading(false);
  };

  const handleResolve = async (issueId) => {
    await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/reconciliation/issues/${issueId}/resolve`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userData.token}`
        },
        body: JSON.stringify({ notes: '' })
      }
    );
    Toast.success('Issue resolved successfully');
    await fetchStats();
    await fetchIssues();
  };

  return (
    <div>
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Issues</div>
            <div className="stat-value">{stats.totalIssues}</div>
          </div>
          <div className="stat-card critical">
            <div className="stat-label">Critical</div>
            <div className="stat-value">{stats.critical}</div>
          </div>
          <div className="stat-card high">
            <div className="stat-label">High Priority</div>
            <div className="stat-value">{stats.high}</div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Resolved Today</div>
            <div className="stat-value">{stats.resolvedToday}</div>
          </div>
        </div>
      )}

      <div className="issue-filters">
        <FormControl className="filter-select">
          <Select
            value={filters.issueType}
            onChange={(e) => setFilters({ ...filters, issueType: e.target.value })}
            displayEmpty
          >
            <MenuItem value="">All Issue Types</MenuItem>
            <MenuItem value="paid_not_active">Paid but Not Active</MenuItem>
            <MenuItem value="active_no_payment">Active but No Payment</MenuItem>
            <MenuItem value="webhook_failed">Webhook Failed</MenuItem>
            <MenuItem value="duplicate_subscription">Duplicate Subscription</MenuItem>
            <MenuItem value="missing_customer">Missing Customer</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="filter-select">
          <Select
            value={filters.severity}
            onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
            displayEmpty
          >
            <MenuItem value="">All Severities</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
      </div>

      {loading && <div className="loading">Loading issues...</div>}

      {!loading && (
        <>
          <div className="issue-list">
            {issues.map((issue) => (
              <div key={issue.issueId} className={`issue-item ${issue.severity}`}>
                <div className="issue-header">
                  <div>
                    <div className="issue-title">{issue.title}</div>
                    <div className="issue-description">{issue.description}</div>
                  </div>
                  <span className={`severity-badge ${issue.severity}`}>
                    {issue.severity}
                  </span>
                </div>

                <div className="issue-meta">
                  <span>Issue ID: {issue.issueId}</span>
                  <span>Type: {issue.issueType}</span>
                  <span>Detected: {new Date(issue.detectedAt).toLocaleString()}</span>
                  {issue.userId && <span>User: {issue.userId}</span>}
                </div>

                {issue.metadata && (
                  <div className="issue-meta" style={{ marginTop: '0.5rem' }}>
                    {Object.entries(issue.metadata).map(([key, value]) => (
                      <span key={key}>
                        <strong>{key}:</strong> {JSON.stringify(value)}
                      </span>
                    ))}
                  </div>
                )}

                {issue.status !== PORTAL_STATUS.RESOLVED && (
                  <div className="resolve-section">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleResolve(issue.issueId)}
                    >
                      Resolve Issue
                    </button>
                  </div>
                )}

                {issue.status === PORTAL_STATUS.RESOLVED && (
                  <div className="resolve-section">
                    <div style={{ color: '#10b981', fontWeight: 600 }}>
                      Resolved {new Date(issue.resolvedAt).toLocaleString()}
                    </div>
                    {issue.resolutionNotes && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#666' }}>
                        {issue.resolutionNotes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {total > 0 && (
            <Pagination
              currentPage={page}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={setPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}
        </>
      )}

      {!loading && issues.length === 0 && (
        <div className="empty-state">
          <DoneIcon className="empty-state-icon" />
          <p>No reconciliation issues found</p>
        </div>
      )}
    </div>
  );
};

export default ReconciliationDashboard;
